import { cn } from "@/lib/utils"
import { useEffect, useRef } from "react"

interface ProcessingItemRowProps {
  articleNumber: string
  isActive: boolean
  onSelect: () => void
  /** Badges/pills shown top-right of the article number (framework/status/edit). */
  topRight?: React.ReactNode
  children?: React.ReactNode
}

/**
 * Shared clickable row shell for the Gaps and Review left panels — same
 * highlight-on-select ring, scroll-into-view-when-active, and header layout,
 * so both screens read as one consistent list instead of two bespoke ones.
 */
export function ProcessingItemRow({
  articleNumber,
  isActive,
  onSelect,
  topRight,
  children,
}: ProcessingItemRowProps) {
  const rowRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (isActive)
      rowRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" })
  }, [isActive])

  return (
    <div
      ref={rowRef}
      onClick={onSelect}
      className={cn(
        "cursor-pointer border-b px-4 py-3 hover:bg-primary/4",
        isActive && "ring-1 ring-inset ring-yellow1",
      )}
    >
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="font-mono text-[12px] text-foreground">
          {articleNumber}
        </span>
        {topRight && (
          <div className="flex shrink-0 items-center gap-1.5">{topRight}</div>
        )}
      </div>
      {children}
    </div>
  )
}
